import Block from '../block'

import gsap from 'gsap'
import debounce from 'lodash.debounce'
import { lockScroll, unlockScroll } from 'scripts/scroll-lock'

// timings from Osmo's Mega Navigation (Directional Hover)
const DURATION = {
	morph: 0.4,
	in: 0.3,
	out: 0.2,
	stagger: 0.25,
	backdropIn: 0.3,
	backdropOut: 0.2,
	open: 0.35,
	close: 0.25,
}

const HOVER_ENTER = 120
const HOVER_LEAVE = 150
const MOBILE_MAX = 1023
const POPUP_RATIO = 0.8

export default class Nav extends Block {
	static title = 'nav'

	constructor($el) {
		super($el)

		this.inner = this.$el.findElement('inner')
		this.list = this.$el.findElement('list')
		this.menu = this.$el.findElement('items')
		this.menuItems = this.$el.findElement('item')
		this.burger = this.$el.findElement('burger')
		this.lines = this.$el.findElement('burger-line')
		this.dropdown = this.$el.findElement('dropdown')
		this.container = this.$el.findElement('dropdown-container')
		this.backdrop = this.$el.findElement('backdrop')
		this.toggles = this.$el.findElement('link--parent')
		this.panels = this.$el.findElement('panel')
		this.subs = this.$el.findElement('sub').filter('[data-id]')
		this.subContents = this.$el.findElement('sub-subs')
		this.search = this.$el.findElement('search')
		this.searchBackdrop = this.$el.findElement('search-backdrop')
		this.dims = this.$el.findElement('dim')

		// desktop dropdown
		this.activeId = null
		this.timeline = null
		this.hoverTimer = null
		this.leaveTimer = null

		// compact menu
		this.menuOpen = false
		this.panelId = null
		this.subId = null
		this.menuTimeline = null
		this.panelTimeline = null

		this.scrollLocked = false
		this.searchOpen = false
		this.pointerType = 'mouse'
		this.width = window.innerWidth

		this.compact = this.isCompact()
		this.compact ? this.resetCompact() : this.resetDesktop()

		this.bindDesktop()
		this.bindCompact()

		$(window).on(
			'resize',
			debounce(() => {
				// ignore height changes, such as a mobile address bar
				if (window.innerWidth !== this.width) {
					this.width = window.innerWidth
					this.applyMode()
				}
			}, 150),
		)
		// logo image and web fonts change the widths after a cold load
		$(window).on('load', () => this.applyMode())
		document.fonts?.ready.then(() => this.applyMode())

		$(document).on('keydown', (e) => this.onEscape(e))

		this.search.on('click', () => this.toggleSearch(!this.searchOpen))
		this.searchBackdrop.on('click', () => this.toggleSearch(false))

		$(document).on('product:add product:remove product:update', (e, data) => {
			this.$el.findElement('count--span').text(data.count)
		})
	}

	bindDesktop() {
		this.toggles
			.on('mouseenter', (e) =>
				this.onToggleEnter($(e.currentTarget).data('id')),
			)
			.on('mouseleave', () => this.onLeave())
			.on('keydown', (e) => this.onToggleKeydown(e))
			.on('click', (e) => this.onToggleClick(e))

		this.dropdown
			.on('mouseenter', () => clearTimeout(this.leaveTimer))
			.on('mouseleave', () => this.onLeave())

		this.subs.on('mouseenter focus', (e) =>
			this.showSubContent($(e.currentTarget)),
		)
		this.panels.on('keydown', (e) => this.onPanelKeydown(e))

		this.backdrop.on('click', () =>
			this.compact ? this.closeMenu() : this.close(),
		)

		$(document).on('click', (e) => {
			if (!this.compact && !$.contains(this.$el.get(0), e.target)) {
				this.close()
			}
		})

		$(document).on('pointerdown', (e) => {
			this.pointerType = e.pointerType || 'mouse'
		})
	}

	bindCompact() {
		this.burger.on('click', () =>
			this.menuOpen ? this.closeMenu() : this.openMenu(),
		)
		this.$el.findElement('back').on('click', () => this.back())

		this.subs.on('click', (e) => {
			const id = $(e.currentTarget).data('id')
			if (this.compact && this.panelId !== null && this.subContent(id).length) {
				e.preventDefault()
				this.openSub(id)
			}
		})
	}

	// compact = mobile width, or desktop width with too many items (nav--popup)
	isCompact() {
		if (window.innerWidth <= MOBILE_MAX) {
			return true
		}

		this.$el.removeModifier('popup')
		const popup =
			this.list.get(0).scrollWidth >= this.inner.innerWidth() * POPUP_RATIO
		if (popup) {
			this.$el.addModifier('popup')
		}

		return popup
	}

	applyMode() {
		const wasCompact = this.compact
		this.compact = this.isCompact()

		if (wasCompact && !this.compact) {
			this.stopMenu()
			this.stopPanel()
			this.set(
				this.menu
					.add(this.menuItems)
					.add(this.panels)
					.add(this.fades(this.panels)),
				{ clearProps: 'all' },
			)
			gsap.set(this.lines, { rotation: 0, y: 0, autoAlpha: 1 })
			this.burger.attr('aria-expanded', 'false')
			this.menuOpen = false
			this.panelId = null
			this.setScrollLock(false)
			this.resetDesktop()
		}

		if (!wasCompact && this.compact) {
			this.stopDropdown()
			clearTimeout(this.hoverTimer)
			clearTimeout(this.leaveTimer)
			this.activeId = null
			this.$el.removeModifier('open')
			this.toggles.attr('aria-expanded', 'false')
			this.dim(this.searchOpen)
			this.resetCompact()
		}
	}

	// Helpers
	panel(id) {
		return this.panels.filter(`[data-id="${id}"]`)
	}

	toggle(id) {
		return this.toggles.filter(`[data-id="${id}"]`)
	}

	subContent(id) {
		return this.subContents.filter(`[data-id="${id}"]`)
	}

	fades($scope) {
		return $scope.find('.nav__fade')
	}

	// fades of the second level: everything except the sub-sub lists
	levelFades($panel) {
		return this.fades($panel).not(this.fades(this.subContents))
	}

	links($panel) {
		return $panel.find('a:visible')
	}

	stagger($targets) {
		return $targets.length > 1 ? { amount: DURATION.stagger } : 0
	}

	// gsap warns about empty targets, e.g. a nav without dropdowns
	set($targets, vars) {
		if ($targets.length) {
			gsap.set($targets, vars)
		}
	}

	setScrollLock(locked) {
		if (locked !== this.scrollLocked) {
			this.scrollLocked = locked
			locked ? lockScroll() : unlockScroll()
		}
	}

	stopDropdown() {
		this.timeline?.kill()
		this.timeline = null
		gsap.killTweensOf(
			this.container
				.add(this.backdrop)
				.add(this.panels)
				.add(this.fades(this.panels)),
		)
	}

	stopMenu() {
		this.menuTimeline?.kill()
		this.menuTimeline = null
		gsap.killTweensOf(this.menu.add(this.backdrop).add(this.lines))
	}

	stopPanel() {
		this.panelTimeline?.kill()
		this.panelTimeline = null
		gsap.killTweensOf(
			this.menuItems
				.add(this.subContents)
				.add(this.panels)
				.add(this.fades(this.panels)),
		)
	}

	resetSubs() {
		this.subs.each((i, el) => $(el).removeModifier('active'))
		this.subContents.each((i, el) => $(el).removeModifier('active'))
		this.panels.each((i, el) => $(el).removeModifier('sub'))
		this.subId = null
	}

	resetDesktop() {
		this.set(this.panels, {
			visibility: 'hidden',
			opacity: 0,
			pointerEvents: 'none',
			x: 0,
			y: 0,
			xPercent: 0,
		})
		this.set(this.fades(this.panels), { autoAlpha: 0, x: 0, y: 0, xPercent: 0 })
		gsap.set(this.container, { height: 0, clearProps: 'transform' })
		gsap.set(this.backdrop, { autoAlpha: 0 })

		this.$el.removeModifier('open')
		this.toggles.attr('aria-expanded', 'false')
		this.resetSubs()
	}

	resetCompact() {
		this.set(this.panels, { autoAlpha: 0, xPercent: 0, pointerEvents: 'none' })
		this.set(this.fades(this.panels), { xPercent: 20, autoAlpha: 0 })
		this.set(this.subContents, { clearProps: 'all' })
		this.set(this.menuItems, { xPercent: 0, y: 0, autoAlpha: 1 })
		gsap.set(this.menu, { autoAlpha: 0, x: 0 })
		gsap.set(this.container, { clearProps: 'height' })
		gsap.set(this.backdrop, { autoAlpha: 0 })

		this.resetSubs()
	}

	// DESKTOP — hover intent
	onToggleEnter(id) {
		// a stray hover must not close the search, a tap or key press does (open)
		if (this.compact || this.searchOpen) return
		clearTimeout(this.leaveTimer)
		clearTimeout(this.hoverTimer)
		this.hoverTimer = setTimeout(
			() => this.open(id),
			this.activeId === null ? HOVER_ENTER : 0,
		)
	}

	onLeave() {
		if (this.compact) return
		clearTimeout(this.hoverTimer)
		this.leaveTimer = setTimeout(() => this.close(), HOVER_LEAVE)
	}

	onToggleClick(e) {
		const id = $(e.currentTarget).data('id')

		if (this.compact) {
			e.preventDefault()
			this.openPanel(id)
			return
		}

		// touch has no hover: the first tap opens the panel, the second follows the link
		if (this.pointerType !== 'mouse' && this.activeId !== id) {
			e.preventDefault()
			clearTimeout(this.hoverTimer)
			this.open(id)
		}
	}

	// DESKTOP — dropdown
	open(id) {
		if (this.activeId === id) return
		this.toggleSearch(false)
		this.dim(true)
		if (this.activeId !== null) return this.switch(this.activeId, id)

		const $panel = this.panel(id)
		const height = $panel.outerHeight()
		if (!height) return

		this.stopDropdown()
		this.resetDesktop()

		const $fades = this.fades($panel)
		this.activeId = id
		this.$el.addModifier('open')
		this.toggle(id).attr('aria-expanded', 'true')

		this.timeline = gsap
			.timeline()
			.to(
				this.backdrop,
				{ autoAlpha: 1, duration: DURATION.backdropIn, ease: 'power2.out' },
				0,
			)
			.to(
				this.container,
				{ height, duration: DURATION.open, ease: 'power3.out' },
				0,
			)
			.set(
				$panel,
				{ visibility: 'visible', opacity: 1, pointerEvents: 'auto' },
				0.05,
			)
			.fromTo(
				$fades,
				{ autoAlpha: 0, y: 8 },
				{
					autoAlpha: 1,
					y: 0,
					duration: DURATION.in,
					stagger: this.stagger($fades),
					ease: 'power3.out',
				},
				0.1,
			)
	}

	close() {
		if (this.activeId === null) return

		const $panel = this.panel(this.activeId)
		this.stopDropdown()
		this.dim(this.searchOpen)

		this.timeline = gsap
			.timeline({
				onComplete: () => {
					this.activeId = null
					this.timeline = null
					this.resetDesktop()
				},
			})
			.to(
				this.fades($panel),
				{
					autoAlpha: 0,
					y: -4,
					duration: DURATION.out * 0.7,
					ease: 'power2.in',
				},
				0,
			)
			.to(
				this.container,
				{ height: 0, duration: DURATION.close, ease: 'power2.in' },
				0.05,
			)
			.to(
				this.backdrop,
				{ autoAlpha: 0, duration: DURATION.backdropOut, ease: 'power2.out' },
				0,
			)
			.set($panel, { visibility: 'hidden', opacity: 0, pointerEvents: 'none' })
	}

	// slides the content in the direction of the hovered toggle
	switch(fromId, toId) {
		const $from = this.panel(fromId)
		const $to = this.panel(toId)
		const height = $to.outerHeight()
		if (!$from.length || !height) return

		const direction =
			this.toggles.index(this.toggle(toId)) >
			this.toggles.index(this.toggle(fromId))
				? 1
				: -1
		const $fromFades = this.fades($from)
		const $toFades = this.fades($to)

		this.stopDropdown()

		// hide every panel, keep the current one visible to fade it out
		gsap.set(this.panels, {
			visibility: 'hidden',
			opacity: 0,
			pointerEvents: 'none',
			xPercent: 0,
		})
		gsap.set(this.fades(this.panels), { autoAlpha: 0, x: 0, y: 0 })
		gsap.set($from, {
			visibility: 'visible',
			opacity: 1,
			pointerEvents: 'auto',
			x: 0,
		})
		gsap.set($fromFades, { autoAlpha: 1, x: 0, y: 0 })
		gsap.set(this.backdrop, { autoAlpha: 1 })

		this.activeId = toId
		this.toggles.attr('aria-expanded', 'false')
		this.toggle(toId).attr('aria-expanded', 'true')

		this.timeline = gsap
			.timeline()
			.to(
				$fromFades,
				{
					autoAlpha: 0,
					x: direction * -30,
					duration: DURATION.out,
					ease: 'power2.in',
				},
				0,
			)
			.set(
				$from,
				{
					visibility: 'hidden',
					opacity: 0,
					pointerEvents: 'none',
					xPercent: 0,
				},
				DURATION.out,
			)
			.set($fromFades, { x: 0 }, DURATION.out)
			.to(
				this.container,
				{ height, duration: DURATION.morph, ease: 'power3.out' },
				0.05,
			)
			.set(
				$to,
				{
					visibility: 'visible',
					opacity: 1,
					pointerEvents: 'auto',
					xPercent: 0,
				},
				DURATION.out * 0.5,
			)
			.fromTo(
				$toFades,
				{ autoAlpha: 0, x: direction * 30 },
				{
					autoAlpha: 1,
					x: 0,
					duration: DURATION.in,
					stagger: this.stagger($toFades),
					ease: 'power3.out',
				},
				DURATION.out * 0.6,
			)
	}

	// reveals the sub-sub list of a hovered or focused sub, morphs the height to fit
	showSubContent($sub) {
		const $panel = this.panels.has($sub.get(0))
		if (
			this.compact ||
			!$panel.is(this.panel(this.activeId)) ||
			$sub.hasModifier('active')
		) {
			return
		}

		this.resetSubs()
		$sub.addModifier('active')

		const $content = this.subContent($sub.data('id'))
		if ($content.length) {
			const $fades = this.fades($content)
			$content.addModifier('active')
			gsap.killTweensOf($fades)
			gsap.fromTo(
				$fades,
				{ autoAlpha: 0, y: 8 },
				{
					autoAlpha: 1,
					y: 0,
					duration: DURATION.in,
					stagger: this.stagger($fades),
					ease: 'power3.out',
				},
			)
		}

		gsap.to(this.container, {
			height: $panel.outerHeight(),
			duration: DURATION.morph,
			ease: 'power3.out',
		})
	}

	// DESKTOP — keyboard, toggles are links: Enter follows the link, Space and ArrowDown open the panel
	onToggleKeydown(e) {
		if (this.compact) return

		const id = $(e.currentTarget).data('id')
		const active = this.activeId === id

		if (e.key === ' ' && active) {
			e.preventDefault()
			this.close()
		} else if (e.key === ' ' || e.key === 'ArrowDown') {
			e.preventDefault()
			this.open(id)
			this.focusFirstLink(id)
		} else if (e.key === 'Tab' && !e.shiftKey && active) {
			e.preventDefault()
			this.links(this.panel(id)).first().trigger('focus')
		}
	}

	focusFirstLink(id) {
		setTimeout(() => {
			const $link = this.links(this.panel(id)).first()
			if ($link.length) {
				gsap.set($link, { visibility: 'visible' })
				$link.trigger('focus')
			}
		}, 80)
	}

	onPanelKeydown(e) {
		if (this.compact || this.activeId === null) return

		const $toggle = this.toggle(this.activeId)
		const $links = this.links(this.panel(this.activeId))
		const index = $links.index(document.activeElement)

		if (e.key === 'ArrowDown') {
			e.preventDefault()
			$links.eq((index + 1) % $links.length).trigger('focus')
		} else if (e.key === 'ArrowUp') {
			e.preventDefault()
			;(index <= 0 ? $toggle : $links.eq(index - 1)).trigger('focus')
		} else if (e.key === 'Tab' && !e.shiftKey && index === $links.length - 1) {
			e.preventDefault()
			const $next = this.toggles.eq(this.toggles.index($toggle) + 1)
			this.close()
			$next.trigger('focus')
		} else if (e.key === 'Tab' && e.shiftKey && index === 0) {
			e.preventDefault()
			$toggle.trigger('focus')
		}
	}

	onEscape(e) {
		if (e.key !== 'Escape') return

		if (this.searchOpen) {
			this.toggleSearch(false)
		} else if (this.compact) {
			if (this.panelId !== null) {
				this.back()
			} else if (this.menuOpen) {
				this.closeMenu()
			}
		} else if (this.activeId !== null) {
			const $toggle = this.toggle(this.activeId)
			this.close()
			$toggle.trigger('focus')
		}
	}

	// COMPACT — menu
	animateBurger(toCross) {
		const [top, middle, bottom] = this.lines.get()
		const timeline = gsap.timeline({ defaults: { ease: 'power2.inOut' } })

		if (toCross) {
			return timeline
				.to(top, { y: '0.3125em', duration: 0.15 }, 0)
				.to(bottom, { y: '-0.3125em', duration: 0.15 }, 0)
				.to(middle, { autoAlpha: 0, duration: 0.1 }, 0.1)
				.to(top, { rotation: 45, duration: 0.2 }, 0.15)
				.to(bottom, { rotation: -45, duration: 0.2 }, 0.15)
		}

		return timeline
			.to([top, bottom], { rotation: 0, duration: 0.2 }, 0)
			.to([top, bottom], { y: 0, duration: 0.15 }, 0.15)
			.to(middle, { autoAlpha: 1, duration: 0.1 }, 0.15)
	}

	openMenu() {
		this.stopMenu()
		this.toggleSearch(false)

		this.menuOpen = true
		this.$el.addModifier('open')
		this.burger.attr('aria-expanded', 'true')
		this.setScrollLock(true)

		this.menuTimeline = gsap
			.timeline()
			.add(this.animateBurger(true), 0)
			.to(this.menu, { autoAlpha: 1, duration: 0.3, ease: 'power2.out' }, 0)
			// the backdrop only shows beside the desktop drawer (nav--popup)
			.to(
				this.backdrop,
				{ autoAlpha: 1, duration: DURATION.backdropIn, ease: 'power2.out' },
				0,
			)
			.fromTo(
				this.menuItems,
				{ autoAlpha: 0, y: 12 },
				{
					autoAlpha: 1,
					y: 0,
					duration: 0.3,
					stagger: 0.04,
					ease: 'power3.out',
				},
				0.15,
			)
	}

	closeMenu() {
		const panelId = this.panelId

		this.stopMenu()
		this.stopPanel()

		this.menuOpen = false
		this.panelId = null
		this.$el.removeModifier('open')
		this.burger.attr('aria-expanded', 'false')

		this.menuTimeline = gsap
			.timeline({
				onComplete: () => {
					this.menuTimeline = null
					this.setScrollLock(false)
					this.resetCompact()
				},
			})
			.add(this.animateBurger(false), 0)
			.to(
				this.menu,
				{ autoAlpha: 0, duration: 0.3, ease: 'power2.inOut' },
				0.05,
			)
			.to(
				this.backdrop,
				{ autoAlpha: 0, duration: DURATION.backdropOut, ease: 'power2.out' },
				0.05,
			)

		// fade an open panel out with the menu, no snap reset
		if (panelId !== null) {
			this.menuTimeline.to(
				this.panel(panelId),
				{ autoAlpha: 0, duration: 0.3, ease: 'power2.inOut' },
				0.05,
			)
		}
	}

	// COMPACT — slide-over levels
	slideForward($out, $in, swap, at) {
		return gsap
			.timeline()
			.to(
				$out,
				{
					xPercent: -10,
					autoAlpha: 0,
					duration: 0.35,
					stagger: 0.03,
					ease: 'power2.in',
				},
				0,
			)
			.add(swap, at)
			.fromTo(
				$in,
				{ xPercent: 8, autoAlpha: 0 },
				{
					xPercent: 0,
					autoAlpha: 1,
					duration: 0.3,
					stagger: this.stagger($in),
					ease: 'power3.out',
				},
				at + 0.05,
			)
	}

	slideBack($out, $in, swap) {
		return gsap
			.timeline()
			.to(
				$out,
				{ xPercent: 20, autoAlpha: 0, duration: 0.3, ease: 'power2.in' },
				0,
			)
			.add(swap, 0.25)
			.fromTo(
				$in,
				{ xPercent: -20, autoAlpha: 0 },
				{
					xPercent: 0,
					autoAlpha: 1,
					duration: 0.35,
					stagger: 0.03,
					ease: 'power3.out',
				},
				0.25,
			)
	}

	openPanel(id) {
		const $panel = this.panel(id)

		this.stopPanel()
		this.panelId = id
		$panel.scrollTop(0)

		this.panelTimeline = this.slideForward(
			this.menuItems,
			this.levelFades($panel),
			() =>
				gsap.set($panel, { autoAlpha: 1, xPercent: 0, pointerEvents: 'auto' }),
			0.2,
		)
	}

	closePanel() {
		const $panel = this.panel(this.panelId)

		this.stopPanel()

		this.panelTimeline = this.slideBack($panel, this.menuItems, () => {
			this.panelId = null
			gsap.set($panel, { autoAlpha: 0, pointerEvents: 'none' })
		})
	}

	openSub(id) {
		const $panel = this.panel(this.panelId)
		const $content = this.subContent(id)

		this.stopPanel()
		this.subId = id

		this.panelTimeline = this.slideForward(
			this.levelFades($panel),
			this.fades($content),
			() => {
				$content.addModifier('active')
				$panel.addModifier('sub').scrollTop(0)
			},
			0.3,
		)
	}

	closeSub() {
		const $panel = this.panel(this.panelId)
		const $content = this.subContent(this.subId)

		this.stopPanel()

		this.panelTimeline = this.slideBack(
			$content,
			this.levelFades($panel),
			() => {
				this.subId = null
				$content.removeModifier('active')
				$panel.removeModifier('sub').scrollTop(0)
				gsap.set($content, { xPercent: 0, autoAlpha: 1 })
			},
		)
	}

	back() {
		this.subId !== null ? this.closeSub() : this.closePanel()
	}

	// bar dim, shared by the dropdown and the search so they crossfade instead of racing
	dim(show) {
		gsap.killTweensOf(this.dims)
		gsap.to(this.dims, {
			autoAlpha: show ? 1 : 0,
			duration: show ? DURATION.backdropIn : DURATION.backdropOut,
			ease: 'power2.out',
		})
	}

	// search overlays the menu and the dropdown, so only one of them is open at a time
	toggleSearch(open) {
		if (open === this.searchOpen) return

		if (open) {
			if (this.menuOpen) {
				this.closeMenu()
			}
			this.close()
		}

		this.searchOpen = open
		this.search.find('i').toggleClass('hidden')
		if (open) {
			this.$el.addModifier('search')
		}

		// own backdrop, so it crossfades with the dropdown backdrop instead of racing it
		// the modifier keeps the toggle's tab, so on close it goes once the fade is done
		this.dim(open)
		gsap.killTweensOf(this.searchBackdrop)
		gsap.to(this.searchBackdrop, {
			autoAlpha: open ? 1 : 0,
			duration: open ? DURATION.backdropIn : DURATION.backdropOut,
			ease: 'power2.out',
			onComplete: open ? null : () => this.$el.removeModifier('search'),
		})

		$(document).trigger('search:toggle', { open })
	}
}
